import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { SiteHeader } from "@/components/site-header";
import { EmptyState } from "@/components/ui/empty-state";
import { ButtonGroup } from "@/components/ui/button-group";
import {
    Search,
    SearchX,
    Shield,
    ShieldCheck,
    Code2,
    UserCheck,
    UserX,
    Users,
    AlertCircle,
    Ban,
    User,
    Copy,
    Check,
    KeyRound,
    Trash2,
    UserPlus,
    Mail,
    Clock,
    Building2
} from 'lucide-react';
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from '@/context/AuthContext';
import { canAccessRootAdmin } from '@/api/root-admin/root-admin.utils';
import { UnauthorizedAccess } from '@/components/auth/UnauthorizedAccess';
import { 
    listDevelopers, 
    listManagers, 
    listPendingRootInvitations,
    cancelRootInvitation
} from "@/api/root-admin/root-admin.api";
import { cn } from "@/lib/utils";
import { InviteMemberDialog } from './components/InviteMemberDialog';
import { ManageAccessDialog } from './components/ManageAccessDialog';
import { DeleteUserDialog } from './components/DeleteUserDialog';
import { PlatformTeamLoading } from './components/PlatformTeamLoading';
import { formatDistanceToNow, isPast } from 'date-fns';

// Combined type for table display
interface TeamMember {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    status: 'active' | 'inactive' | 'pending_invitation';
    created_at: string;
    access_grants_count: number | null;
    // For pending invitations
    expires_at?: string;
    invitation_id?: string;
}

// Role display mapping
const roleDisplayMap: Record<string, { label: string; color: string; icon: typeof Shield }> = {
    super_admin: {
        label: "Super Admin",
        color: "bg-red-50 text-red-700 border-red-200/80 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20",
        icon: ShieldCheck
    },
    manager: {
        label: "Manager",
        color: "bg-purple-50 text-purple-700 border-purple-200/80 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20",
        icon: Shield
    },
    developer: {
        label: "Developer",
        color: "bg-blue-50 text-blue-700 border-blue-200/80 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
        icon: Code2
    }
};

const formatRole = (role: string) => {
    const roleInfo = roleDisplayMap[role] || {
        label: role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        color: "bg-gray-50 text-gray-700 border-gray-200/80 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20",
        icon: Shield
    };

    return (
        <div className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
            roleInfo.color
        )}>
            <roleInfo.icon className="h-3.5 w-3.5" />
            {roleInfo.label}
        </div>
    );
};

export default function PlatformTeamPage() {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
    
    // Dialog states
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [accessDialogOpen, setAccessDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
    const [cancellingInvitation, setCancellingInvitation] = useState<string | null>(null);

    const { error, success } = useEnhancedToast();
    const errorRef = useRef(error);
    const successRef = useRef(success);
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            
            // Fetch all data in parallel
            const [developersRes, managersRes, invitationsRes] = await Promise.all([
                listDevelopers(),
                listManagers(),
                listPendingRootInvitations()
            ]);

            const allMembers: TeamMember[] = [];

            // Add developers
            if (developersRes.data) {
                developersRes.data.forEach(dev => {
                    allMembers.push({
                        ...dev,
                        status: dev.status === 'active' ? 'active' : 'inactive'
                    });
                });
            }

            // Add managers
            if (managersRes.data) {
                managersRes.data.forEach(mgr => {
                    allMembers.push({
                        ...mgr,
                        status: mgr.status === 'active' ? 'active' : 'inactive'
                    });
                });
            }

            // Add pending invitations that aren't already in the list
            if (invitationsRes.data) {
                const existingEmails = new Set(allMembers.map(m => m.email.toLowerCase()));
                invitationsRes.data.forEach(inv => {
                    if (!existingEmails.has(inv.email.toLowerCase())) {
                        allMembers.push({
                            id: inv.id,
                            email: inv.email,
                            first_name: '',
                            last_name: '',
                            role: inv.role,
                            status: 'pending_invitation',
                            created_at: inv.created_at,
                            access_grants_count: null,
                            expires_at: inv.expires_at,
                            invitation_id: inv.id
                        });
                    }
                });
            }

            setMembers(allMembers);

            if (developersRes.error || managersRes.error) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Failed to Load Some Data",
                    description: "Could not fetch all team members",
                    duration: 4000,
                });
            }
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Error Loading Data",
                description: err instanceof Error ? err.message : "Could not fetch team data",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Check if user has access - must be after all hooks
    if (!user || !canAccessRootAdmin(user.role)) {
        return <UnauthorizedAccess />;
    }

    // Filter members
    const filteredMembers = members.filter(member => {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
            member.email?.toLowerCase().includes(searchLower) ||
            `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase().includes(searchLower);
        
        const matchesRole = !roleFilter || member.role === roleFilter;
        const matchesStatus = !statusFilter || member.status === statusFilter;
        
        return matchesSearch && matchesRole && matchesStatus;
    });

    const handleManageAccess = (member: TeamMember) => {
        setSelectedMember(member);
        setAccessDialogOpen(true);
    };

    const handleDeleteUser = (member: TeamMember) => {
        setSelectedMember(member);
        setDeleteDialogOpen(true);
    };

    const handleCancelInvitation = async (invitationId: string) => {
        setCancellingInvitation(invitationId);
        try {
            const result = await cancelRootInvitation(invitationId);
            if (result.error) {
                throw new Error(result.error);
            }
            successRef.current({
                icon: Check,
                title: "Invitation Cancelled",
                description: "The invitation has been cancelled",
                duration: 3000,
            });
            fetchData();
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Cancel Failed",
                description: err instanceof Error ? err.message : "Could not cancel invitation",
                duration: 4000,
            });
        } finally {
            setCancellingInvitation(null);
        }
    };

    const handleClearFilters = () => {
        setRoleFilter("");
        setStatusFilter("");
        setSearchQuery("");
    };

    // Filter options
    const roleOptions = [
        { label: "All Roles", value: "" },
        { label: "Manager", value: "manager" },
        { label: "Developer", value: "developer" },
    ];

    const statusOptions = [
        { label: "All Status", value: "" },
        { label: "Active", value: "active" },
        { label: "Pending Signup", value: "inactive" },
        { label: "Pending Invitation", value: "pending_invitation" },
    ];

    if (isLoading) {
        return <PlatformTeamLoading />;
    }

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Platform Team"
                subtitle="Manage developers and managers with organization access"
            >
                {/* Only show filters and invite button when members exist */}
                {members.length > 0 && (
                    <>
                        {/* Search */}
                        <div className="relative w-[250px] lg:w-[300px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search by name or email..."
                                className="h-9 pl-9"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {/* Role Filter */}
                        <Combobox
                            options={roleOptions}
                            value={roleFilter}
                            onValueChange={(val) => setRoleFilter(val || "")}
                            placeholder="Filter by role..."
                            className="w-[160px]"
                        />

                        {/* Status Filter */}
                        <Combobox
                            options={statusOptions}
                            value={statusFilter}
                            onValueChange={(val) => setStatusFilter(val || "")}
                            placeholder="Filter by status..."
                            className="w-[180px]"
                        />

                        {/* Invite Button */}
                        <Button 
                            size="sm"
                            onClick={() => setInviteDialogOpen(true)}
                            className="gap-2"
                        >
                            <UserPlus className="h-4 w-4" />
                            Invite Member
                        </Button>
                    </>
                )}
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                {members.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyState
                                icon={Users}
                                title="No Team Members Yet"
                                description="Get started by inviting developers or managers to the platform team"
                                action={{
                                    label: "Invite Member",
                                    onClick: () => setInviteDialogOpen(true),
                                }}
                            />
                        </motion.div>
                    </div>
                ) : (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                    className="px-4 py-6 md:px-8 md:py-8"
                >
                    {filteredMembers.length === 0 ? (
                            <div 
                                className="flex items-center justify-center text-center"
                                style={{ height: 'calc(100vh - 250px)' }}
                            >
                                <div className="flex flex-col items-center">
                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                        <SearchX className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-lg font-semibold mb-2">No Matching Members</h3>
                                    <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                                        No team members found matching your search and filter criteria.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleClearFilters}
                                        className="gap-2"
                                    >
                                        <Ban className="h-4 w-4" />
                                        Clear All Filters
                                    </Button>
                                </div>
                            </div>
                    ) : (
                        <div className="rounded-md border shadow-sm">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="w-[280px]">User</TableHead>
                                        <TableHead className="w-[130px]">Role</TableHead>
                                        <TableHead className="w-[150px]">Status</TableHead>
                                        <TableHead className="w-[150px]">Organizations</TableHead>
                                        <TableHead className="w-[140px] text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredMembers.map((member) => {
                                        const hasSignedUp = member.first_name && member.last_name;
                                        const initials = hasSignedUp 
                                            ? `${member.first_name[0]}${member.last_name[0]}`.toUpperCase()
                                            : null;
                                        const isPendingInvitation = member.status === 'pending_invitation';
                                        const isExpired = isPendingInvitation && member.expires_at && isPast(new Date(member.expires_at));
                                        
                                        return (
                                            <TableRow key={member.id} className="group">
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <Avatar className="h-9 w-9">
                                                            <AvatarFallback className={cn(
                                                                "text-xs font-semibold",
                                                                hasSignedUp 
                                                                    ? "bg-primary/10 text-primary" 
                                                                    : isPendingInvitation
                                                                        ? "bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400"
                                                                        : "bg-muted text-muted-foreground"
                                                            )}>
                                                                {hasSignedUp ? initials : isPendingInvitation ? <Mail className="h-4 w-4" /> : <User className="h-4 w-4" />}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className="flex flex-col min-w-0">
                                                            {hasSignedUp ? (
                                                                <span className="font-medium truncate">
                                                                    {member.first_name} {member.last_name}
                                                                </span>
                                                            ) : (
                                                                <span className="text-sm italic text-muted-foreground truncate">
                                                                    {isPendingInvitation ? 'Invitation sent' : 'Pending signup'}
                                                                </span>
                                                            )}
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm text-muted-foreground truncate">
                                                                    {member.email}
                                                                </span>
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigator.clipboard.writeText(member.email);
                                                                        setCopiedEmail(member.id);
                                                                        setTimeout(() => setCopiedEmail(null), 2000);
                                                                    }}
                                                                >
                                                                    {copiedEmail === member.id ? (
                                                                        <Check className="h-3 w-3 text-green-500" />
                                                                    ) : (
                                                                        <Copy className="h-3 w-3" />
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {formatRole(member.role)}
                                                </TableCell>
                                                <TableCell>
                                                    {isPendingInvitation ? (
                                                        <div className="space-y-1">
                                                            <div className={cn(
                                                                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                                                                isExpired
                                                                    ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/50 dark:text-red-400 dark:border-red-800/20"
                                                                    : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-800/20"
                                                            )}>
                                                                <Mail className="h-3.5 w-3.5" />
                                                                {isExpired ? 'Expired' : 'Invited'}
                                                            </div>
                                                            {member.expires_at && (
                                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                    <Clock className="h-3 w-3" />
                                                                    {isExpired 
                                                                        ? 'Expired'
                                                                        : formatDistanceToNow(new Date(member.expires_at), { addSuffix: true })
                                                                    }
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className={cn(
                                                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                                                            member.status === 'active' 
                                                                ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/50 dark:text-green-400 dark:border-green-800/20"
                                                                : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-800/20"
                                                        )}>
                                                            {member.status === 'active' ? (
                                                                <>
                                                                    <UserCheck className="h-3.5 w-3.5" />
                                                                    Active
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <UserX className="h-3.5 w-3.5" />
                                                                    Pending
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {!isPendingInvitation && (
                                                        <div className="flex items-center gap-2">
                                                            <Building2 className="h-4 w-4 text-muted-foreground" />
                                                            <span className="text-sm font-medium">
                                                                {member.access_grants_count ?? 0}
                                                            </span>
                                                            <span className="text-sm text-muted-foreground">
                                                                org{(member.access_grants_count ?? 0) !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center justify-end">
                                                        {isPendingInvitation ? (
                                                            <TooltipProvider delayDuration={200}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                            onClick={() => member.invitation_id && handleCancelInvitation(member.invitation_id)}
                                                                            disabled={cancellingInvitation === member.invitation_id}
                                                                        >
                                                                            {cancellingInvitation === member.invitation_id ? (
                                                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                                                            ) : (
                                                                                <Trash2 className="h-4 w-4" />
                                                                            )}
                                                                            Cancel
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Cancel Invitation</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        ) : (
                                                            <ButtonGroup>
                                                                {/* Manage Access Button */}
                                                                <TooltipProvider delayDuration={200}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="outline"
                                                                                size="icon"
                                                                                className="h-8 w-8"
                                                                                onClick={() => handleManageAccess(member)}
                                                                            >
                                                                                <KeyRound className="h-4 w-4" />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Manage Access</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>

                                                                {/* Delete Button */}
                                                                <TooltipProvider delayDuration={200}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="outline"
                                                                                size="icon"
                                                                                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                                onClick={() => handleDeleteUser(member)}
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Delete User</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </ButtonGroup>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </motion.div>
                )}
            </div>

            {/* Dialogs */}
            <InviteMemberDialog
                open={inviteDialogOpen}
                onOpenChange={setInviteDialogOpen}
                onSuccess={fetchData}
            />

            <ManageAccessDialog
                open={accessDialogOpen}
                onOpenChange={setAccessDialogOpen}
                user={selectedMember ? {
                    id: selectedMember.id,
                    email: selectedMember.email,
                    first_name: selectedMember.first_name,
                    last_name: selectedMember.last_name,
                    role: selectedMember.role,
                    status: selectedMember.status === 'active' ? 'active' : 'inactive',
                    created_at: selectedMember.created_at,
                    access_grants_count: selectedMember.access_grants_count
                } : null}
                onSuccess={fetchData}
            />

            <DeleteUserDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                user={selectedMember}
                onSuccess={fetchData}
            />
        </div>
    );
}
